/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';

// Store
import useStore from '@store/index';

// Types
import {
	TApiResponseResetPassword,
	TBodyResetPassword,
} from '../types';
import { TApiError } from '@global-types/api/api';

// Constants
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const resetPassword = (
	body: TBodyResetPassword
): Promise<TApiResponseResetPassword> => {
	return apiClient.post('/users/reset_password', {
		...body,
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useResetPassword = () => {
	const { t } = useTranslation();

	const { setUser: setUser } = useStore();
	const navigate = useNavigate();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: resetPassword,
		onSuccess: (data) => {
			notifications.show({
				title: t('global.success'),
				message: t('auth.passwordSuccessfullyUpdated'),
				color: 'success',
			});
			queryClient.setQueryData(
				[queryKeys.auth.user],
				data
			);
			setUser(data);
			navigate('/');
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
